<script>
import { required } from 'vuelidate/lib/validators';
import ItemCategory from "@/components/modals/item-category.vue";
import FundClusterList from "@/components/modals/fund-cluster-list.vue";
import Unit from "@/components/modals/unit.vue";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import MaskedInput from "vue-text-mask";

export default {
    name: "StockModal",
    components: {
        ItemCategory,
        Unit,
        MaskedInput,
        FundClusterList
    },
    props: {
        modalTitle: {
            type: String,
            default: "Stock",
        },
    },
    validations: {
        form: {
            stockNumber: { required },
            name: { required },
            unitId: { required },
            itemCategoryId: { required },
            fundClusterId: { required },
        }
    },
    data() {
        return {
            form: {
                campusId: null,
                id: 0,
                stockNumber: "",
                name: "",
                description: "",
                unitId: "",
                itemCategoryId: "",
                iarItemId: 0,
                subsidiaryLedgerId: 0,
                fundClusterId: 0,
                unitCost: null
            },
            submitted: false,
            isSubmitting: false,
            unit: null,
            itemCategory: null,
            fundCluster: null,
            currencyMask: createNumberMask({
                prefix: "",
                allowDecimal: true
            }),
        };
    },
    methods: {
        resetForm() {
            this.form = {
                campusId: null,
                id: 0,
                stockNumber: "",
                name: "",
                description: "",
                unitId: "",
                itemCategoryId: "",
                iarItemId: 0,
                subsidiaryLedgerId: 0,
                fundClusterId: 0,
                unitCost: ''
            };
            this.submitted = false;
            this.isSubmitting = false;
            this.$v.$reset();
        },
        updateData(data) {
            this.form = JSON.parse(JSON.stringify(data));
            this.form.unitCost = this.form.unitCost ? this.form.unitCost.toString() : '';
            this.unit = data.unit ? data.unit : null;
            this.itemCategory = data.itemCategory ? data.itemCategory : null;
            this.fundCluster = data.fundCluster ? data.fundCluster : null;
            this.submitted = false;
            this.isSubmitting = false;
        },
        async submitForm() {
            this.submitted = true;
            this.$v.form.$touch();
            if (this.$v.form.$invalid) {
                return;
            }
            let data = JSON.parse(JSON.stringify(this.form));
            data.subsidiaryLedgerId = this.form.subsidiaryLedgerId || 0;
            data.unitCost = this.getExactAmt(this.form.unitCost || 0);
            data.fundCluster = this.fundCluster ? this.fundCluster : null;
            this.isSubmitting = true;
            this.$emit("submit", data);
        },
        getItemCategory() {
            this.$refs.itemcategory.filterStockCategory();
            this.$bvModal.show("item-category-modal");
        },
        dropItemCat(data) {
            this.form.itemCategoryId = data.id;
            this.itemCategory = data.name;
        },
        getUnit() {
            this.$refs.unit.getData();
            this.$bvModal.show("unit-modal");
        },
        dropUnit(data) {
            this.form.unitId = data.id;
            this.unit = data.unitName;
        },
        getFundCluster() {
            this.$refs.fundcluster.getData();
            this.$bvModal.show("fund-cluster-modal");
        },
        dropFundCluster(data) {
            this.form.fundClusterId = data.id;
            this.fundCluster = data.cluster;
        },
    },
};
</script>

<template>
    <b-modal
        id="stock-modal"
        :title="modalTitle"
        size="md"
        title-class="font-18"
        hide-footer
        no-close-on-backdrop
    >
        <form @submit.prevent="submitForm" class="needs-validation">
            <div class="mb-3">
                <label for="stockNumber" class="form-label">
                    Stock Number 
                </label>
                <b-form-input
                    id="stockNumber"
                    v-model="form.stockNumber"
                    type="text"
                    placeholder="Enter stock number"
                    autocomplete="off"
                    :class="{
                        'is-invalid': submitted && $v.form.stockNumber.$error,
                    }"
                ></b-form-input>
                <div
                    v-if="submitted && $v.form.stockNumber.$error"
                    class="invalid-feedback"
                >
                    <span v-if="!$v.form.stockNumber.required">This value is required.</span>
                </div>
            </div>
            <div class="mb-3">
                <label for="name" class="form-label">
                    Name 
                </label>
                <b-form-input
                    id="name"
                    v-model="form.name"
                    type="text"
                    placeholder="Enter stock name"
                    autocomplete="off"
                    :class="{
                        'is-invalid': submitted && $v.form.name.$error,
                    }"
                ></b-form-input>
                <div
                    v-if="submitted && $v.form.name.$error"
                    class="invalid-feedback"
                >
                    <span v-if="!$v.form.name.required">This value is required.</span>
                </div>
            </div>
            <div class="mb-3">
                <label for="unit">Unit </label>
                <b-input-group>
                    <template #append>
                        <b-button @click="getUnit" variant="outline-info"
                            ><i class="mdi mdi-clipboard-text-search-outline"></i
                        ></b-button>
                    </template>
                    <input
                        autocomplete="off"
                        type="text"
                        disabled
                        placeholder="Search Unit..."
                        class="form-control"
                        :class="{
                            'is-invalid': submitted && $v.form.unitId.$error
                        }"
                        v-model="unit"
                    />
                </b-input-group>
                <div
                    v-if="submitted && $v.form.unitId.$error"
                    class="invalid-feedback"
                >
                    <span v-if="!$v.form.unitId.required">This value is required.</span>
                </div>
            </div>
            <div class="mb-3">
                <label for="itemCategoryId">Fund Cluster </label>
                <b-input-group>
                    <template #append>
                    <b-button @click="getFundCluster" variant="outline-info"
                        ><i class="mdi mdi-clipboard-text-search-outline"></i
                    ></b-button>
                    </template>
                    <input
                        autocomplete="off"
                        type="text"
                        disabled
                        placeholder="Search Fund Cluster..."
                        class="form-control"
                        :class="{
                            'is-invalid': submitted && $v.form.fundClusterId.$error
                        }"
                        v-model="fundCluster"
                    />
                </b-input-group>
                <div
                    v-if="submitted && $v.form.fundClusterId.$error"
                    class="invalid-feedback"
                >
                    <span v-if="!$v.form.fundClusterId.required"
                    >This value is required.</span
                    >
                </div>
            </div>
            <div class="mb-3">
                <label for="itemCategoryId">Item Category </label>
                <b-input-group>
                    <template #append>
                    <b-button @click="getItemCategory" variant="outline-info"
                        ><i class="mdi mdi-clipboard-text-search-outline"></i
                    ></b-button>
                    </template>
                    <input
                        autocomplete="off"
                        type="text"
                        disabled
                        placeholder="Search Item Category..."
                        class="form-control"
                        :class="{
                            'is-invalid': submitted && $v.form.itemCategoryId.$error
                        }"
                        v-model="itemCategory"
                    />
                </b-input-group>
                <div
                    v-if="submitted && $v.form.itemCategoryId.$error"
                    class="invalid-feedback"
                >
                    <span v-if="!$v.form.itemCategoryId.required"
                    >This value is required.</span
                    >
                </div>
            </div>
            <div class="mb-3">
                <label for="unitCost" >
                    Unit Cost 
                </label>
                <MaskedInput
                    id="unitCost"
                    v-model="form.unitCost"
                    :mask="currencyMask"
                    type="text"
                    class="form-control"
                    placeholder="Enter unit cost"
                    autocomplete="off"
                />
            </div>
            <div class="mb-3">
                <label for="description" class="form-label">
                    Description
                </label>
                <b-form-textarea
                    id="description"
                    v-model="form.description"
                    placeholder="Enter description (optional)"
                    rows="3"
                    autocomplete="off"
                ></b-form-textarea>
            </div>
            <div class="modal-footer">
                <button type="submit" class="btn btn-primary"><i class="bx bx-save me-1"></i>{{ form.id != 0 ? "Update" : "Save" }} Stock</button>
                <button type="button" class="btn btn-link" @click="$bvModal.hide('stock-modal')">Close</button>
            </div>
        </form>
        <item-category
            ref="itemcategory"
            @dropData="dropItemCat($event)"
        ></item-category>
        <unit ref="unit" @dropData="dropUnit($event)"></unit>
        <FundClusterList ref="fundcluster" @dropData="dropFundCluster($event)"></FundClusterList>
    </b-modal>
</template>